import { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  LinearProgress,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { EmptyState, ErrorState, LoadingState } from '@/shared/ui';
import { formatQuantity } from '@/shared/lib/quantityFormat';

import { useDeleteStoreScope, useLocationGroups, useRetailStores, useStoreScopes, useUpsertStoreScope } from '../../api/hooks';
import type { RetailStoreOption } from '../../api/storesApi';
import type { AllocationMode, AllocationType, BufferType, LocationGroup, StoreScope } from '../../model/types';
import { StoreDerivationPanel } from './StoreDerivationPanel';

interface StoresPanelProps {
  canManage: boolean;
  onToast: (message: string, severity?: 'success' | 'error' | 'warning') => void;
  onOpenOverrides: (storeCode: string) => void;
}

/** "Stores" tab — the relocated store-to-stock-source editor (design-stock-channels-page §8.3-§8.5). */
export function StoresPanel({ canManage, onToast, onOpenOverrides }: StoresPanelProps) {
  const { t } = useTranslation('channelStock');
  const scopes = useStoreScopes();
  const groups = useLocationGroups();
  const stores = useRetailStores();
  const upsert = useUpsertStoreScope();
  const remove = useDeleteStoreScope();

  const [dialogStore, setDialogStore] = useState<{ store: RetailStoreOption; scope: StoreScope | null } | null>(null);
  const [confirmUnbind, setConfirmUnbind] = useState<StoreScope | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const groupById = useMemo(() => new Map((groups.data ?? []).map((g) => [g.id, g])), [groups.data]);

  if (scopes.isLoading || groups.isLoading || stores.isLoading) return <LoadingState />;
  if (scopes.isError) return <ErrorState onRetry={() => void scopes.refetch()} />;
  if (stores.isError) return <ErrorState onRetry={() => void stores.refetch()} />;

  // Review fix C1 (spec §8.3): EVERY retail store renders a row, joined with its scope when
  // present — a scope for a store no longer in the directory still renders (defensive tail).
  const scopeByCode = new Map((scopes.data ?? []).map((scope) => [scope.storeCode, scope]));
  const directory = stores.data ?? [];
  const orphanScopes = (scopes.data ?? []).filter(
    (scope) => !directory.some((store) => store.code === scope.storeCode),
  );
  const rows: { store: RetailStoreOption; scope: StoreScope | null }[] = [
    ...directory.map((store) => ({ store, scope: scopeByCode.get(store.code) ?? null })),
    ...orphanScopes.map((scope) => ({ store: { code: scope.storeCode, name: scope.storeCode }, scope })),
  ];

  const modeChip = (scope: StoreScope) => {
    const label = t(`stores.mode.${scope.allocationMode.toLowerCase()}`);
    const caption =
      scope.allocationMode === 'ALLOCATED' && scope.allocationValue != null
        ? scope.allocationType === 'PERCENT'
          ? t('stores.quotaPercent', { value: formatQuantity(scope.allocationValue) })
          : t('stores.quotaQty', { value: formatQuantity(scope.allocationValue) })
        : null;
    return (
      <Stack spacing={0}>
        <Chip size="small" variant="outlined" label={label} sx={{ width: 'fit-content' }} />
        {caption ? (
          <Typography variant="caption" color="text.secondary">{caption}</Typography>
        ) : null}
      </Stack>
    );
  };

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between">
        <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
          {t('stores.hint')}
        </Typography>
        <Tooltip title={t('refresh')}>
          <IconButton
            onClick={() => {
              void scopes.refetch();
              void stores.refetch();
            }}
            aria-label="refresh-stores"
          >
            <RefreshRoundedIcon />
          </IconButton>
        </Tooltip>
      </Stack>
      {scopes.isFetching && !scopes.isLoading ? <LinearProgress sx={{ height: 4 }} /> : <span style={{ height: 4, display: 'block' }} />}

      {rows.length === 0 ? (
        <EmptyState
          icon={<TuneRoundedIcon />}
          title={t('stores.empty.title')}
          description={t('stores.empty.description')}
        />
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('stores.columns.store')}</TableCell>
                <TableCell>{t('stores.columns.source')}</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{t('stores.columns.mode')}</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{t('stores.columns.buffer')}</TableCell>
                <TableCell>{t('stores.columns.warning')}</TableCell>
                <TableCell align="right">{t('columns.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(({ store, scope }) => {
                const group = scope ? groupById.get(scope.groupId) : undefined;
                const isExpanded = expanded === store.code;
                return (
                  <Fragment key={store.code}>
                    <TableRow hover>
                      <TableCell>
                        <Typography sx={{ fontWeight: 600 }}>{store.name}</Typography>
                        <Typography variant="caption" sx={{ fontFamily: '"JetBrains Mono", monospace' }} color="text.secondary">
                          {store.code}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {scope ? (
                          group?.name ?? scope.groupId
                        ) : (
                          <Typography variant="body2" color="text.secondary">{t('stores.sourceUnset')}</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{scope ? modeChip(scope) : '—'}</TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        {scope?.displayBuffer != null
                          ? `${formatQuantity(scope.displayBuffer)} ${scope.bufferType === 'PERCENT' ? '%' : t('stores.bufferUnits')}`
                          : '—'}
                      </TableCell>
                      <TableCell>
                        {scope?.warning ? (
                          <Tooltip title={scope.warning}>
                            <Chip
                              size="small"
                              color="warning"
                              variant="outlined"
                              icon={<WarningAmberRoundedIcon />}
                              label={t('stores.mirrorWarningChip')}
                            />
                          </Tooltip>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title={canManage ? t('stores.configure') : t('noPermissionTooltip')}>
                          <span>
                            <IconButton
                              size="small"
                              disabled={!canManage}
                              aria-label={`configure-store-${store.code}`}
                              onClick={() => setDialogStore({ store, scope })}
                            >
                              <EditRoundedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        {scope ? (
                          <>
                            <Tooltip title={t('stores.overridesShortcut')}>
                              <IconButton size="small" aria-label={`overrides-${store.code}`} onClick={() => onOpenOverrides(store.code)}>
                                <TuneRoundedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={canManage ? t('stores.unbind') : t('noPermissionTooltip')}>
                              <span>
                                <IconButton size="small" disabled={!canManage} aria-label={`unbind-store-${store.code}`} onClick={() => setConfirmUnbind(scope)}>
                                  <DeleteOutlineRoundedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <IconButton
                              size="small"
                              aria-label={`expand-store-${store.code}`}
                              aria-expanded={isExpanded}
                              onClick={() => setExpanded(isExpanded ? null : store.code)}
                              sx={{ transform: isExpanded ? 'rotate(180deg)' : undefined }}
                            >
                              <ExpandMoreRoundedIcon fontSize="small" />
                            </IconButton>
                          </>
                        ) : null}
                      </TableCell>
                    </TableRow>
                    {scope ? (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ py: 0, borderBottom: isExpanded ? undefined : 'none' }}>
                          <Collapse in={isExpanded} unmountOnExit>
                            <StoreDerivationPanel scope={scope} group={group} />
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {dialogStore !== null ? (
        <StoreScopeDialog
          store={dialogStore.store}
          scope={dialogStore.scope}
          groups={groups.data ?? []}
          pending={upsert.isPending}
          onClose={() => setDialogStore(null)}
          onSave={(storeCode, body) =>
            upsert.mutate(
              { storeCode, body },
              {
                onSuccess: (saved) => {
                  setDialogStore(null);
                  if (saved.warning) {
                    onToast(t('stores.savedWithWarning', { warning: saved.warning }), 'warning');
                  } else {
                    onToast(t('stores.saved'));
                  }
                },
                onError: (error) => onToast(error.message, 'error'),
              },
            )
          }
        />
      ) : null}

      <Dialog
        open={confirmUnbind !== null}
        onClose={remove.isPending ? undefined : () => setConfirmUnbind(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{t('stores.unbindConfirmTitle')}</DialogTitle>
        <DialogContent>
          <Typography>{t('stores.unbindConfirm')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmUnbind(null)}>{t('cancel')}</Button>
          <Button
            color="error"
            variant="contained"
            disabled={remove.isPending}
            startIcon={remove.isPending ? <CircularProgress size={16} /> : undefined}
            onClick={() => {
              if (!confirmUnbind) return;
              remove.mutate(confirmUnbind.storeCode, {
                onSuccess: () => {
                  setConfirmUnbind(null);
                  onToast(t('stores.unbound'));
                },
                onError: (error) => onToast(error.message, 'error'),
              });
            }}
          >
            {t('stores.unbindAction')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

interface StoreScopeDialogProps {
  store: RetailStoreOption;
  scope: StoreScope | null;
  groups: LocationGroup[];
  pending: boolean;
  onClose: () => void;
  onSave: (
    storeCode: string,
    body: {
      groupId: string;
      allocationMode: AllocationMode;
      allocationType: AllocationType | null;
      allocationValue: number | null;
      displayBuffer: number | null;
      bufferType: BufferType;
    },
  ) => void;
}

function StoreScopeDialog({ store, scope, groups, pending, onClose, onSave }: StoreScopeDialogProps) {
  const { t } = useTranslation('channelStock');
  const storeCode = store.code;
  const [groupId, setGroupId] = useState(scope?.groupId ?? '');
  const [mode, setMode] = useState<AllocationMode>(scope?.allocationMode ?? 'DEDICATED');
  const [allocationType, setAllocationType] = useState<AllocationType>(scope?.allocationType ?? 'FIXED_QTY');
  const [allocationValue, setAllocationValue] = useState(
    scope?.allocationValue != null ? String(scope.allocationValue) : '',
  );
  const [buffer, setBuffer] = useState(scope?.displayBuffer != null ? String(scope.displayBuffer) : '');
  const [bufferType, setBufferType] = useState<BufferType>(scope?.bufferType ?? 'QTY');

  const allocationValid =
    mode !== 'ALLOCATED' ||
    (allocationValue.trim() !== '' &&
      Number(allocationValue) > 0 &&
      (allocationType !== 'PERCENT' || Number(allocationValue) <= 100));
  const valid = groupId !== '' && allocationValid && (buffer.trim() === '' || Number(buffer) >= 0);

  return (
    <Dialog open fullWidth maxWidth="md" onClose={pending ? undefined : onClose}>
      <DialogTitle>
        {t('stores.dialogEditTitle', { store: store.name })}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label={t('stores.codeLabel')}
            value={storeCode}
            disabled
            helperText={store.name}
            slotProps={{ htmlInput: { style: { fontFamily: '"JetBrains Mono", monospace' } } }}
          />
          <TextField
            select
            required
            label={t('channels.groupLabel')}
            value={groupId}
            onChange={(event) => setGroupId(event.target.value)}
            helperText={t('stores.groupHelper')}
          >
            {groups.map((group) => (
              <MenuItem key={group.id} value={group.id}>
                {group.name} ({t('groups.locationsCount', { count: group.locationIds.length })})
              </MenuItem>
            ))}
          </TextField>
          <RadioGroup value={mode} onChange={(event) => setMode(event.target.value as AllocationMode)}>
            <FormControlLabel value="DEDICATED" control={<Radio />} label={
              <Stack spacing={0}>
                <Typography>{t('stores.mode.dedicated')}</Typography>
                <Typography variant="caption" color="text.secondary">{t('stores.mode.dedicatedHint')}</Typography>
              </Stack>
            } />
            <FormControlLabel value="ALLOCATED" control={<Radio />} label={
              <Stack spacing={0}>
                <Typography>{t('stores.mode.allocated')}</Typography>
                <Typography variant="caption" color="text.secondary">{t('stores.mode.allocatedHint')}</Typography>
              </Stack>
            } />
            {mode === 'ALLOCATED' ? (
              <Stack direction="row" spacing={2} sx={{ pl: 4, pb: 1 }}>
                <TextField
                  select
                  size="small"
                  label={t('stores.quotaTypeLabel')}
                  value={allocationType}
                  onChange={(event) => setAllocationType(event.target.value as AllocationType)}
                  sx={{ minWidth: 180 }}
                >
                  <MenuItem value="FIXED_QTY">{t('stores.quotaType.fixed')}</MenuItem>
                  <MenuItem value="PERCENT">{t('stores.quotaType.percent')}</MenuItem>
                </TextField>
                <TextField
                  size="small"
                  label={t('stores.quotaValueLabel')}
                  type="number"
                  value={allocationValue}
                  onChange={(event) => setAllocationValue(event.target.value)}
                  slotProps={{ htmlInput: { min: 0, max: allocationType === 'PERCENT' ? 100 : undefined, step: 'any' } }}
                  error={!allocationValid}
                />
              </Stack>
            ) : null}
            <FormControlLabel value="MIRROR" control={<Radio />} label={
              <Stack spacing={0}>
                <Typography>{t('stores.mode.mirror')}</Typography>
                <Typography variant="caption" color="text.secondary">{t('stores.mode.mirrorHint')}</Typography>
              </Stack>
            } />
            {mode === 'MIRROR' ? (
              <Alert severity="warning" sx={{ ml: 4, mb: 1 }}>
                {scope?.warning
                  ? `${scope.warning} ${t('stores.mirrorRecommendation')}`
                  : t('stores.mirrorWarningFallback')}
              </Alert>
            ) : null}
          </RadioGroup>
          <Stack direction="row" spacing={2}>
            <TextField
              label={t('stores.bufferLabel')}
              type="number"
              value={buffer}
              onChange={(event) => setBuffer(event.target.value)}
              slotProps={{ htmlInput: { min: 0, step: 'any' } }}
              helperText={t('stores.bufferHelper')}
              sx={{ flex: 1 }}
            />
            <TextField
              select
              label={t('stores.bufferTypeLabel')}
              value={bufferType}
              onChange={(event) => setBufferType(event.target.value as BufferType)}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="QTY">{t('stores.bufferType.qty')}</MenuItem>
              <MenuItem value="PERCENT">{t('stores.bufferType.percent')}</MenuItem>
            </TextField>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={pending}>{t('cancel')}</Button>
        <Button
          variant="contained"
          disabled={!valid || pending}
          startIcon={pending ? <CircularProgress size={16} /> : undefined}
          onClick={() =>
            onSave(storeCode, {
              groupId,
              allocationMode: mode,
              allocationType: mode === 'ALLOCATED' ? allocationType : null,
              allocationValue: mode === 'ALLOCATED' ? Number(allocationValue) : null,
              displayBuffer: buffer.trim() === '' ? null : Number(buffer),
              bufferType,
            })
          }
        >
          {t('save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
